import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../website/components/WebsiteLayout';
import Reveal from '../website/components/Reveal';
import { useLanguage } from '../../hooks/useLanguage';

const LinkedinIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.65-1.85 3.39-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26ZM5.34 7.43a2.07 2.07 0 1 1 0-4.15 2.07 2.07 0 0 1 0 4.15ZM7.12 20.45H3.55V9h3.57v11.45Z" />
  </svg>
);

const AboutUs: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "If you have a real problem we might be the right company for, we'd like to hear about it."
          : 'Heb je een echt probleem waar wij het juiste bedrijf voor zouden kunnen zijn? Dan horen we het graag.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta} hidePartners>
      <Helmet>
        <title>About · Thoughtful Oasis</title>
        <meta
          name="description"
          content="A small international team building interpretable AI from the Netherlands. Mission, founders, and how we work."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="A small international team based in Enschede, building interpretable AI for Dutch public service and European regulated sectors."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/about" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
      </Helmet>

      {/* Hero with HQ-27 background */}
      <header className="hero hero--photo">
        <div className="hero__bg">
          <img
            src="/assets/website/photos/hq-27.webp"
            alt="Founding team picture of Thoughtful Oasis, including Kaspar Rothenfusser, Bekk Blando, and Rens Dommerholt"
          />
        </div>
        <div className="hero__inner">
          <span className="label">{en ? 'About' : 'Over ons'}</span>
          <h1 style={{ marginTop: 20 }}>
            {en
              ? 'Building AI that puts trust first.'
              : 'AI bouwen die vertrouwen op de eerste plaats stelt.'}
          </h1>
          <p className="hero__sub">
            {en
              ? 'An international team based in the Netherlands. Science, engineering, and delivery under one roof.'
              : 'Een internationaal team gevestigd in Nederland. Wetenschap, engineering en oplevering onder één dak.'}
          </p>
        </div>
      </header>

      {/* Mission */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head prose">
            <span className="label">{en ? 'Mission' : 'Missie'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en
                ? 'AI we can understand. Systems we can trust.'
                : 'AI die we kunnen begrijpen. Systemen die we kunnen vertrouwen.'}
            </h2>
            <p className="body-text" style={{ marginTop: 24 }}>
              {en
                ? 'Machine learning is extraordinary at processing volume, finding patterns, and scaling beyond what any person could do alone. But the best decisions happen when that power is combined with human judgement — not hidden from it.'
                : 'Machine learning is uitzonderlijk goed in het verwerken van volume, het vinden van patronen, en schalen voorbij wat één persoon alleen kan. Maar de beste beslissingen ontstaan wanneer die kracht wordt gecombineerd met menselijk oordeelsvermogen — niet ervoor verborgen.'}
            </p>
            <p className="body-text" style={{ marginTop: 16 }}>
              <span className="hl">
                {en
                  ? 'Our mission: unify what makes machine learning powerful with what makes human expertise irreplaceable — and build systems that are trustworthy by design, not by afterthought.'
                  : 'Onze missie: samenvoegen wat machine learning krachtig maakt met wat menselijke expertise onvervangbaar maakt — en systemen bouwen die betrouwbaar zijn van opzet, niet achteraf.'}
              </span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founders */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">Team</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Who we are.' : 'Wie we zijn.'}
            </h2>
          </Reveal>

          <div className="team">
            <Reveal className="team__card">
              <img
                className="team__photo"
                src="/assets/website/photos/kaspar.webp"
                alt="Kaspar Rothenfusser"
              />
              <p className="team__name">Kaspar Rothenfusser</p>
              <p className="team__role">Chief Science Officer</p>
              <p className="team__line">
                {en
                  ? 'Sets the research direction — what we study, what we publish, and how that feeds the product.'
                  : 'Bepaalt de onderzoeksrichting: wat we bestuderen, wat we publiceren, en hoe dat het product voedt.'}
              </p>
              <a
                className="team__social"
                href="https://www.linkedin.com/in/kaspar-rothenfusser/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinIcon />
                <span>LinkedIn</span>
              </a>
            </Reveal>
            <Reveal className="team__card">
              <img className="team__photo" src="/assets/website/photos/bekk.webp" alt="Bekk Blando" />
              <p className="team__name">Bekk Blando</p>
              <p className="team__role">Chief Technology Officer</p>
              <p className="team__line">
                {en
                  ? 'Builds the platform that runs in production. Owns architecture, deployment, and the API.'
                  : 'Bouwt het platform dat in productie draait. Verantwoordelijk voor architectuur, deployment en de API.'}
              </p>
              <a
                className="team__social"
                href="https://www.linkedin.com/in/bekk-blando-a3b97aa9/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinIcon />
                <span>LinkedIn</span>
              </a>
            </Reveal>
            <Reveal className="team__card">
              <img
                className="team__photo"
                src="/assets/website/photos/rens.webp"
                alt="Rens Dommerholt"
              />
              <p className="team__name">Rens Dommerholt</p>
              <p className="team__role">Chief Executive Officer</p>
              <p className="team__line">
                {en
                  ? 'Runs the company: sales, partnerships, compliance, and delivery.'
                  : 'Runt het bedrijf: sales, partnerships, compliance en oplevering.'}
              </p>
              <a
                className="team__social"
                href="https://www.linkedin.com/in/rens-dommerholt/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedinIcon />
                <span>LinkedIn</span>
              </a>
            </Reveal>
          </div>

          <Reveal className="section-head prose" style={{ marginTop: 56 }}>
            <h3
              style={{
                fontFamily: 'var(--f-display)',
                fontWeight: 500,
                fontSize: '1.25rem',
                margin: 0,
              }}
            >
              {en ? "We're looking for talent." : 'We zoeken talent.'}
            </h3>
            <p className="body-text" style={{ marginTop: 12 }}>
              {en
                ? "If you care about interpretable AI and want to work on problems that matter, we'd like to hear from you. Research, engineering, or applied — reach out."
                : 'Als je geeft om interpreteerbare AI en wilt werken aan problemen die ertoe doen, horen we graag van je. Onderzoek, engineering of toegepast — neem contact op.'}
            </p>
            <p style={{ marginTop: 16 }}>
              <Link className="link" to="/contact?prefill=talent">
                {en ? 'Get in touch' : 'Neem contact op'}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* How we work */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head prose">
            <span className="label">{en ? 'How we work' : 'Hoe we werken'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'What collaboration looks like.' : 'Hoe samenwerking eruitziet.'}
            </h2>
            <p className="body-text" style={{ marginTop: 24 }}>
              {en
                ? "It starts with a conversation: your data, someone in your team who knows the field, a problem off-the-shelf doesn't solve. Your expert explains what matters in your work: equipment, era, jurisdiction; or urgency, risk, citizen impact. The system builds an ontology from that. We test it on real cases until the output is something you can defend to a board, procurement, or an auditor. Then it goes live behind your existing tools, we train your team, and ownership stays with you."
                : 'Het begint met een gesprek: jullie data, iemand uit jullie team die het vakgebied kent, een probleem dat een standaardoplossing niet aankan. Jullie expert legt uit wat ertoe doet in het werk: apparatuur, tijdsperiode, jurisdictie; of urgentie, risico, impact op burgers. De AI-laag bouwt daaruit een ontologie op. We testen op echte cases tot de output iets is dat je kan verdedigen tegenover een bestuur, inkoop, of een auditor. Dan gaat het live achter jullie bestaande tools, trainen we jullie team, en blijft het eigenaarschap bij jullie.'}
            </p>
            <p className="body-text" style={{ marginTop: 16 }}>
              {en
                ? "We publish generalized insights about the core technology, never about a specific partner. What we learn on other projects benefits yours; what's specific to your work stays inside the room."
                : 'We publiceren gegeneraliseerde inzichten over de kerntechnologie, nooit over een specifieke partner. Wat we leren op andere projecten komt jullie ten goede; wat specifiek is voor jullie werk blijft binnen de muren.'}
            </p>
          </Reveal>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default AboutUs;
